import { useLayoutEffect, useRef, useState } from 'react'

export const FIT_LINES_MAX_PX = 22
export const FIT_LINES_MIN_PX = 14

interface Options {
  maxPx?: number
  /** Запас по высоте в долях размера шрифта: в строке бывает элемент выше неё (плашка). */
  extraEm?: number
}

/**
 * Подбирает размер текста, при котором он занимает не больше `maxLines` строк: от `maxPx`
 * вниз, шагом 1px, до 14px. Меряем по высоте, а не по числу символов: перенос зависит от ширины и
 * самих слов. `maxLines` не задан — размер обычный. Текст длиннее лимита при 14px остаётся в
 * большем числе строк: обрезать его нельзя.
 */
export const useFitLines = (
  text: string,
  maxLines: number | undefined,
  { maxPx = FIT_LINES_MAX_PX, extraEm = 0 }: Options = {},
) => {
  const ref = useRef<HTMLParagraphElement>(null)
  const [fontSize, setFontSize] = useState(maxPx)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || maxLines === undefined) return
    const fit = () => {
      let size = maxPx
      for (; size > FIT_LINES_MIN_PX; size -= 1) {
        el.style.fontSize = `${size}px`
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
        const fitsHeight = el.scrollHeight <= lineHeight * maxLines + size * extraEm + 1
        // Неразрывная плашка в строке может оказаться шире текста.
        const fitsWidth = el.scrollWidth <= el.clientWidth + 1
        if (!lineHeight || (fitsHeight && fitsWidth)) break
      }
      el.style.fontSize = `${size}px`
      setFontSize(size)
    }
    fit()
    // Ширина экрана меняется и после монтирования (поворот, панель редактора). Высоту не
    // слушаем: её меняет сам подбор, и наблюдатель зациклился бы.
    let width = el.getBoundingClientRect().width
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const next = el.getBoundingClientRect().width
      if (next !== width) {
        width = next
        fit()
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, maxLines, maxPx, extraEm])

  return { ref, fontSize: maxLines === undefined ? undefined : fontSize }
}
